import { Download } from "components/icons/generated";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import CodeSnippetBlock from "ds/components/CodeSnippetBlock";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import DrawerSimple from "ds/components/DrawerNew/Simple";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import Typography from "ds/components/Typography";
import useAnalytics from "hooks/useAnalytics";
import useDownloadCpuProfile from "shared/Run/useDownloadCpuProfile";
import { getCpuProfileFileName } from "shared/Run/useDownloadCpuProfile/helpers";
import { RunState } from "types/generated";

import InstructionSteps from "../InstructionSteps";

type CpuProfileDrawerProps = {
  stackId: string;
  runId: string;
  phase: RunState;
  runState: RunState;
};

const CpuProfileDrawer = createDrawer(
  ({ stackId, runId, phase, runState }: CpuProfileDrawerProps) => {
    const trackSegmentAnalyticsEvent = useAnalytics();

    const { downloadCpuProfile, loading } = useDownloadCpuProfile({
      phase,
      runId,
      stackId,
      runState,
    });

    const fileName = getCpuProfileFileName(runId, phase);
    const command = `go tool pprof -http=:8080 \\
  ${fileName}`;
    const copyCommand = `go tool pprof -http=:8080 ${fileName}`;

    return (
      <DrawerSimple>
        <DrawerHeader justify="between">
          <DrawerHeaderTitle title="Download CPU profile" />
          <DrawerCloseIcon />
        </DrawerHeader>
        <DrawerBody fullHeight gap="x-large">
          <Banner variant="info">
            The profile downloads as a single pprof file with the CPU samples OpenTofu recorded
            during the {phase.toLowerCase()} phase.
          </Banner>
          <Box direction="column" gap="large">
            <Typography tag="h3" variant="p-t7">
              Open it with pprof
            </Typography>
            <InstructionSteps
              steps={[
                "Install Go if you don't have it. It includes pprof.",
                "Run this in the folder you saved the file to:",
              ]}
            />
            <CodeSnippetBlock
              language="shell"
              allowCopy
              copyTitle="Copy command"
              copyValue={copyCommand}
              onCopy={() => trackSegmentAnalyticsEvent("Run Logs - copy CPU profile command")}
            >
              {command}
            </CodeSnippetBlock>
            <InstructionSteps
              start={3}
              steps={[
                "In the browser tab pprof opens, go to View → Flame Graph to see where the time went.",
              ]}
            />
          </Box>
        </DrawerBody>
        <DrawerFooter>
          <DrawerFooterActions>
            <Button
              variant="primary"
              startIcon={Download}
              onPress={downloadCpuProfile}
              loading={loading}
            >
              Download CPU profile
            </Button>
          </DrawerFooterActions>
        </DrawerFooter>
      </DrawerSimple>
    );
  }
);

export const showCpuProfileDrawer = createDrawerTrigger(CpuProfileDrawer);
